import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { IndicatorFactType } from '@/api/rest/resources/types/fact';
import {
  GET_DASHBOARD_WIDGET_CONFIGURATION_PATH,
  GetDashboardWidgetConfigurationErrorDto,
  GetDashboardWidgetConfigurationSuccessDto,
  WidgetType,
} from '@/api/rest/resources/types/widget';
import { DEFAULT_WIDGET_CONFIGURATION_BY_WIDGET_NAME } from '@/pages/shared/hooks/useDefaultWidgetProps';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

type SuccessDto = GetDashboardWidgetConfigurationSuccessDto;
type ErrorDto = GetDashboardWidgetConfigurationErrorDto;

export const useWidgetConfigurationByOrganizationId = (organizationIdArg?: string | null) => {
  const orgId = useOrganizationId();

  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_DASHBOARD_WIDGET_CONFIGURATION_PATH, { organization_id: organizationId });
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['dashboard', 'widget', 'configuration', organizationId],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useHiddenFactConfigurationByFactType = (factType: IndicatorFactType) => {
  const organizationWidgetConfiguration = useWidgetConfigurationByOrganizationId().data;
  const hiddenFacts = organizationWidgetConfiguration ? organizationWidgetConfiguration.hidden_facts : [];

  if (hiddenFacts.length === 0) return false;

  return hiddenFacts.includes(factType);
};

export const useHiddenFactConfigurationByFactTypes = (factTypes: IndicatorFactType[]): IndicatorFactType[] => {
  const organizationWidgetConfiguration = useWidgetConfigurationByOrganizationId().data;
  const hiddenFacts = organizationWidgetConfiguration ? organizationWidgetConfiguration.hidden_facts : [];

  if (hiddenFacts.length === 0) return [];

  return hiddenFacts.filter((f) => factTypes.includes(f));
};

export const useWidgetSlotConfigurationByNameType = (name: string, type: WidgetType) => {
  const organizationWidgetConfiguration = useWidgetConfigurationByOrganizationId().data;
  const widgetConfigurations = organizationWidgetConfiguration.configurations;

  const defaultWidgetConfiguration = useDefaultWidgetSlotConfigurationByName(name);

  const widgetSlots = widgetConfigurations.filter((widget) => widget.name === name && widget.type === type);

  // if widget name is "default" and the config does not return an override for "default",
  // then we check return the default widget config stored in FE
  // eg: show default dashboard, if no custom config found for the organization
  if (widgetSlots.length === 0 && name.includes('default')) {
    return defaultWidgetConfiguration;
  }

  if (widgetSlots.length === 0) {
    return undefined;
  }

  if (widgetSlots.length > 1) {
    return undefined;
  }

  return widgetSlots.at(0);
};

const useDefaultWidgetSlotConfigurationByName = (name: string) => {
  if (!(name in DEFAULT_WIDGET_CONFIGURATION_BY_WIDGET_NAME)) {
    return undefined;
  }

  const getterWidgetConfiguration =
    DEFAULT_WIDGET_CONFIGURATION_BY_WIDGET_NAME[`${name}` as keyof typeof DEFAULT_WIDGET_CONFIGURATION_BY_WIDGET_NAME];
  return getterWidgetConfiguration();
};
